// @boty/database - Supabase client, types, and query utilities
// Implemented in Story 1.3 (Supabase Setup)
// Auth utilities added in Story 1.7

// Client functions for creating Supabase connections (query operations)
export {
  createBrowserClient,
  createServerClient,
  createAnonClient,
  /** @deprecated Use createAnonClient instead */
  createSessionClient,
} from './client';

// Auth utilities (SSR-compatible with httpOnly cookies)
// NOTE: For server-side auth (createAuthServerClient), use @boty/database/server
export {
  // Browser-side auth client
  createAuthBrowserClient,
  // Auth functions (client-side)
  signUp,
  signIn,
  signOut,
  resetPassword,
  getUser,
  resendVerificationEmail,
  // React context provider and hook
  AuthProvider,
  useAuth,
} from './auth';

// Auth types
// Note: AuthUser is the Supabase auth user, User is the database user record
export type {
  AuthResult,
  SignUpData,
  SignInData,
  AuthContextValue,
  AuthUser,
  AuthError,
  Session,
  OAuthProvider,
  SignInWithOAuthOptions,
} from './auth';

// Re-export Supabase types
export type { SupabaseClient, Database } from './client';

// Database types and type aliases
export type {
  Json,
  User,
  UserInsert,
  UserUpdate,
  Bot,
  BotInsert,
  BotUpdate,
  BotStatus,
  BotVisibility,
  BotContent,
  BotContentInsert,
  BotContentUpdate,
  SourceType,
  RefreshInterval,
  RefreshStatus,
  ContentRefreshLog,
  ContentRefreshLogInsert,
  CronJobLog,
  CronJobLogInsert,
  Message,
  MessageInsert,
  MessageUpdate,
  MessageRole,
  MessageChannel,
  MessageChatType,
  Feedback,
  FeedbackInsert,
  FeedbackUpdate,
  FeedbackRating,
  BotSlugRedirect,
  BotSlugRedirectInsert,
  // Analytics types (Story 6.5)
  AnalyticsEvent,
  AnalyticsEventInsert,
  AnalyticsEventUpdate,
  AnalyticsEventType,
  // Feedback submission types (Story 16.4)
  FeedbackSubmission,
  FeedbackSubmissionInsert,
  FeedbackSubmissionUpdate,
  FeedbackSubmissionType,
  FeedbackSubmissionStatus,
  // Telegram integration types (Story 15.1)
  TelegramIntegration,
  TelegramIntegrationInsert,
  TelegramIntegrationUpdate,
  TelegramIntegrationStatus,
  // Telegram settings types (Story 15.4)
  TelegramCommand,
  TelegramGroupBehavior,
  // WhatsApp types (Story 17.1)
  WhatsappSession,
  WhatsappSessionInsert,
  WhatsappSessionUpdate,
  WhatsappCreatorPhone,
  WhatsappCreatorPhoneInsert,
  WhatsappCreatorPhoneUpdate,
  // Creator messages types (Story 4.11)
  CreatorMessage,
  CreatorMessageInsert,
  CreatorMessageUpdate,
  // Intake questions types (Story 7.6)
  IntakeQuestion,
  IntakeQuestionInsert,
  IntakeQuestionUpdate,
  IntakeResponse,
  IntakeResponseInsert,
  IntakeResponseUpdate,
  IntakeSession,
  IntakeSessionInsert,
  IntakeSessionUpdate,
} from './types';

// Query utilities
export {
  // Bot queries
  getBotBySlug,
  getBotById,
  getUserBots,
  getSessionBots,
  createBot,
  updateBot,
  deactivateBot,
  publishBot,
  claimBot,
  // Bot content queries
  getBotContent,
  addBotContent,
  updateBotContent,
  deleteBotContent,
  reorderBotContent,
  getNextDisplayOrder,
  getBotTokenCount,
  estimateTokenCount,
  // Message queries
  getChatHistory,
  addMessage,
  getRecentMessages,
  // Feedback queries
  addFeedback,
  getBotFeedback,
  getBotFeedbackStats,
  // Session utilities
  generateSessionId,
  sessionOwnBot,
  // Slug redirect queries
  createSlugRedirect,
  findBotByOldSlug,
  getBotSlugRedirects,
  // SEO queries
  getPublishedBotSlugs,
  // Sample/demo bot queries
  getSampleBots,
  // Feedback submission queries (Story 16.4)
  addFeedbackSubmission,
  getFeedbackSubmissions,
  updateFeedbackSubmission,
  countRecentSubmissions,
  // Suggested questions queries (Story 4.10)
  updateBotSuggestedQuestions,
  getBotSuggestedQuestions,
  getTopQuestions,
  // Suggested questions enable/disable (Story 7.9)
  setBotSuggestedQuestionsEnabled,
} from './queries';

// Telegram integration queries (Story 15.1)
export {
  getTelegramIntegration,
  upsertTelegramIntegration,
  deleteTelegramIntegration,
} from './queries/telegramIntegrations';

// WhatsApp session queries (Story 17.1)
export {
  getWhatsAppSession,
  upsertWhatsAppSession,
  deleteWhatsAppSession,
  getWhatsAppCreatorPhone,
  getCreatorByPhone,
  upsertWhatsAppCreatorPhone,
  deleteWhatsAppCreatorPhone,
} from './queries/whatsappSessions';

// Viral metrics queries (Story 6.5)
export {
  calculateViralCoefficient,
  getKFactorTrend,
  getTopReferringBots,
  getReferralFunnel,
} from './queries/viralMetrics';
export type {
  ViralMetrics,
  WeeklyKFactor,
  TopReferringBot,
  ReferralFunnel,
  DateRange,
} from './queries/viralMetrics';

// Creator messages queries (Story 4.11)
export {
  insertCreatorMessage,
  getCreatorMessagesByBotId,
  countCreatorMessagesBySession,
  markCreatorMessageNotified,
} from './queries/creatorMessages';

// Intake questions queries (Story 7.6)
export {
  getIntakeQuestions,
  createIntakeQuestion,
  updateIntakeQuestion,
  deleteIntakeQuestion,
  bulkUpdateIntakeQuestions,
  countIntakeQuestions,
  getNextIntakeQuestionOrder,
} from './queries/intakeQuestions';

// Intake responses queries (Story 7.6)
export {
  createIntakeResponse,
  getIntakeResponsesByBot,
  upsertIntakeSession,
  confirmIntakeSession,
  markIntakeSessionNotified,
  getPendingIntakeTimeouts,
  getIntakeResponsesForSession,
} from './queries/intakeResponses';

// Milestones module (Story 9.2)
export {
  MILESTONE_THRESHOLDS,
  getMilestoneTypesForMetric,
  getEligibleMilestones,
  getMilestoneLabel,
  getMilestoneThresholdValue,
  // Milestone check functions (for API integration)
  checkQuestionMilestones,
  checkFeedbackMilestones,
  triggerMilestoneNotification,
} from './milestones';
export type {
  MilestoneType,
  Milestone,
  MilestoneInsert,
  MilestoneUpdate,
  MilestoneAwardResult,
  PendingMilestone,
  MilestoneWithBot,
  MilestoneThreshold,
} from './milestones';
